// Ported from KoPilot (same RBAC admin screens).
// Feature permissions tree of the role modal, reproducing the reference
// admin's FeaturePermissionsTree (cinzan-edu-admin): clickable permission
// chips, a "check all" box and an x/n counter per feature, and a search
// that keeps a feature when its name — or a descendant's — matches.
// The checkboxes themselves are the form fields (role[feature_permission_ids][]).

const normalizeText = (str) => (str || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")

export function initPermissionTrees() {
  document.querySelectorAll("[data-permission-tree]").forEach((tree) => {
    if (tree.dataset.permissionTreeWired) return
    tree.dataset.permissionTreeWired = "true"
    setupPermissionTree(tree)
  })
}

function setupPermissionTree(tree) {
  const form = tree.closest("form")
  const search = tree.querySelector("[data-permission-tree-search]")
  const clear = tree.querySelector("[data-permission-tree-clear]")
  const empty = tree.querySelector("[data-permission-tree-empty]")
  const reset = form.querySelector("[data-permission-tree-reset]")

  const ownPermissionInputs = (node) =>
    Array.from(node.querySelector(":scope > .permission-feature-card [data-feature-permissions]")?.querySelectorAll("input[type=checkbox]") || [])

  // Chip, label, check-all box and counter follow the checkboxes' state.
  const refresh = () => {
    tree.querySelectorAll("[data-permission-chip]").forEach((chip) => {
      const input = chip.querySelector("input")
      const label = chip.querySelector("label")
      const color = chip.dataset.color
      chip.classList.toggle(`bg-${color}`, input.checked)
      chip.classList.toggle("bg-opacity-10", input.checked)
      chip.classList.toggle(`border-${color}`, input.checked)
      chip.classList.toggle("bg-white", !input.checked)
      chip.classList.toggle("border-light", !input.checked)
      label.classList.toggle(`text-${color}`, input.checked)
      label.classList.toggle("fw-semibold", input.checked)
      label.classList.toggle("text-muted", !input.checked)
    })

    tree.querySelectorAll("[data-feature-node]").forEach((node) => {
      const inputs = ownPermissionInputs(node)
      if (!inputs.length) return
      const checkedCount = inputs.filter((input) => input.checked).length
      const fullyChecked = checkedCount === inputs.length
      const checkAll = node.querySelector(":scope > .permission-feature-card [data-feature-check-all]")
      const counter = node.querySelector(":scope > .permission-feature-card [data-feature-counter]")
      if (checkAll) {
        checkAll.checked = fullyChecked
        checkAll.style.opacity = checkedCount > 0 && !fullyChecked ? 0.6 : 1
      }
      if (counter) counter.textContent = `${checkedCount}/${inputs.length}`
    })
  }

  tree.addEventListener("click", (event) => {
    const chip = event.target.closest("[data-permission-chip]")
    if (!chip || event.target.closest("input, label")) return // the box/label toggle themselves
    const input = chip.querySelector("input")
    if (input.disabled) return
    input.checked = !input.checked
    refresh()
  })

  tree.addEventListener("change", (event) => {
    const checkAll = event.target.closest("[data-feature-check-all]")
    if (checkAll) {
      const node = checkAll.closest("[data-feature-node]")
      ownPermissionInputs(node).forEach((input) => { if (!input.disabled) input.checked = checkAll.checked })
    }
    refresh()
  })

  // Filled by the form modal (table_list.js) when opening it for a role.
  form.addEventListener("form-modal:filled", () => {
    if (search) search.value = ""
    applySearch()
    refresh()
  })

  reset?.addEventListener("click", () => {
    tree.querySelectorAll("[data-feature-permissions] input[type=checkbox]").forEach((input) => {
      if (!input.disabled) input.checked = false
    })
    refresh()
  })

  // A node is shown when its name matches (with its whole subtree) or when
  // one of its descendants matches (with only the matching branches).
  const filterNode = (node, term) => {
    const nameMatches = normalizeText(node.dataset.name).includes(term)
    const children = Array.from(node.querySelectorAll(":scope > .permission-feature-card > div > .mt-3 > [data-feature-node]"))
    if (nameMatches) {
      node.hidden = false
      node.querySelectorAll("[data-feature-node]").forEach((descendant) => { descendant.hidden = false })
      return true
    }
    const anyChild = children.map((child) => filterNode(child, term)).some(Boolean)
    node.hidden = !anyChild
    return anyChild
  }

  const applySearch = () => {
    const term = normalizeText(search?.value.trim())
    if (clear) clear.hidden = !term
    const roots = Array.from(tree.querySelectorAll("[data-feature-node]")).filter((node) => !node.parentElement.closest("[data-feature-node]"))
    if (!term) {
      tree.querySelectorAll("[data-feature-node]").forEach((node) => { node.hidden = false })
      if (empty) empty.hidden = true
      return
    }
    const shown = roots.map((root) => filterNode(root, term))
    if (empty) empty.hidden = shown.some(Boolean)
  }

  search?.addEventListener("input", applySearch)
  // Enter in the search box must filter, not submit the role form.
  search?.addEventListener("keydown", (event) => {
    if (event.key !== "Enter") return
    event.preventDefault()
    applySearch()
  })
  clear?.addEventListener("click", () => {
    search.value = ""
    applySearch()
  })

  refresh()
}
